/**
 * Enhanced dream sentencization using Compromise.js for syntactic awareness
 * Falls back to regex-based approach for robustness
 */

import nlp from 'compromise';
import { DreamSegment, DreamSentencizer } from './dream-sentencizer.js';

export interface EnhancedSegmentationMetrics {
  dreamText: string;
  timestamp: number;
  parserUsed: 'compromise' | 'regex-fallback';
  segmentCount: number;
  avgSegmentLength: number;
  processingTimeMs: number;
  compromiseFailed?: boolean;
  failureReason?: string;
}

export class EnhancedDreamSentencizer {
  private static metrics: EnhancedSegmentationMetrics[] = [];

  /**
   * Split dream text using syntactic parsing with fallback to regex
   */
  static sentencize(
    dreamText: string,
    options: {
      minSegmentLength?: number;
      maxSegmentLength?: number;
      useCompromise?: boolean;
      logMetrics?: boolean;
    } = {}
  ): DreamSegment[] {
    const {
      minSegmentLength = 15,
      maxSegmentLength = 300,
      useCompromise = true,
      logMetrics = false,
    } = options;

    const startTime = performance.now();
    console.log(`📝 Enhanced sentencizing: "${dreamText.slice(0, 60)}..."`);

    let segments: DreamSegment[] = [];
    let parserUsed: 'compromise' | 'regex-fallback' = 'regex-fallback';
    let compromiseFailed = false;
    let failureReason: string | undefined;

    // Step 1: Try Compromise.js for intelligent clause detection
    if (useCompromise) {
      try {
        segments = this.parseWithCompromise(dreamText, minSegmentLength, maxSegmentLength);
        
        if (segments.length > 0) {
          parserUsed = 'compromise';
          console.log(`   ✅ Compromise parsed ${segments.length} segments`);
        } else {
          console.log('   ⚠️ Compromise returned no segments, using fallback');
          failureReason = 'no_segments_returned';
        }
      } catch (error) {
        compromiseFailed = true;
        failureReason = error instanceof Error ? error.message : 'unknown_error';
        console.warn('⚠️ Compromise failed, falling back to regex:', error);
      }
    }

    // Step 2: Fallback to existing regex-based approach
    if (segments.length === 0) {
      console.log('   ⏪ Using fallback regex parser');
      segments = DreamSentencizer.sentencize(dreamText, {
        minSegmentLength,
        maxSegmentLength,
      });
      parserUsed = 'regex-fallback';
    }

    const processingTimeMs = performance.now() - startTime;
    console.log(`   ⏱️ Processing time: ${processingTimeMs.toFixed(2)}ms`);

    // Log metrics if enabled
    if (logMetrics) {
      this.logMetrics({
        dreamText,
        timestamp: Date.now(),
        parserUsed,
        segmentCount: segments.length,
        avgSegmentLength: segments.reduce((sum, s) => sum + s.text.length, 0) / segments.length,
        processingTimeMs,
        compromiseFailed,
        failureReason,
      });
    }

    return segments;
  }

  /**
   * Parse using Compromise.js - syntactically aware
   */
  private static parseWithCompromise(
    dreamText: string,
    minLength: number,
    maxLength: number
  ): DreamSegment[] {
    const doc = nlp(dreamText);
    const segments: DreamSegment[] = [];

    // Get sentences first
    const sentences = doc.sentences().json();

    if (!sentences || sentences.length === 0) {
      // Compromise failed to parse - return empty to trigger fallback
      return [];
    }

    for (const sentence of sentences) {
      const sentenceText = sentence.text;

      // Try to split into clauses (don't pass minLength - we'll handle short segments later)
      const clauses = this.extractClauses(sentenceText);

      if (clauses.length > 1) {
        // Successfully split into multiple clauses
        for (const clause of clauses) {
          // Split long clauses
          if (clause.length > maxLength) {
            const splitClauses = this.splitLongSentence(clause, minLength, maxLength);
            for (const subClause of splitClauses) {
              segments.push({
                text: subClause,
                index: segments.length,
                type: 'clause',
                originalPosition: this.findPosition(subClause, dreamText),
              });
            }
          } else {
            // Add clause regardless of length - let mergeShortSegments handle it
            segments.push({
              text: clause,
              index: segments.length,
              type: 'clause',
              originalPosition: this.findPosition(clause, dreamText),
            });
          }
        }
      } else {
        // Single clause/sentence
        // If sentence is too long, try to split it
        if (sentenceText.length > maxLength) {
          const splitClauses = this.splitLongSentence(sentenceText, minLength, maxLength);
          for (const clause of splitClauses) {
            segments.push({
              text: clause,
              index: segments.length,
              type: 'clause',
              originalPosition: this.findPosition(clause, dreamText),
            });
          }
        } else {
          // Add sentence regardless of length - let mergeShortSegments handle it
          segments.push({
            text: sentenceText,
            index: segments.length,
            type: 'sentence',
            originalPosition: this.findPosition(sentenceText, dreamText),
          });
        }
      }
    }

    // Post-process: merge very short segments
    return this.mergeShortSegments(segments, minLength);
  }

  /**
   * Extract clauses using Compromise's clause detection + enhanced splitting
   */
  private static extractClauses(sentenceText: string): string[] {
    const sentenceDoc = nlp(sentenceText);
    
    // Use Compromise's native clause detection first
    const clausesJson = sentenceDoc.clauses().json();
    
    if (clausesJson && clausesJson.length > 0) {
      // Further split each clause on coordinating conjunctions if needed
      const furtherSplit: string[] = [];
      
      for (const clauseObj of clausesJson) {
        // Use the terms that Compromise already parsed (with proper tags!)
        const subClauses = this.splitClauseOnConjunctions(clauseObj);
        furtherSplit.push(...subClauses);
      }
      
      return furtherSplit.filter(t => t.length > 0);
    }
    
    // No clause split - parse the whole sentence ourselves
    return this.splitOnCoordinatingConjunctions(sentenceText);
  }

  /**
   * Split a Compromise clause object on coordinating conjunctions
   * Uses the already-parsed terms with proper tags
   */
  private static splitClauseOnConjunctions(clauseObj: any): string[] {
    const terms = clauseObj.terms || [];
    
    if (terms.length === 0) {
      return [clauseObj.text];
    }
    
    const clauses: string[] = [];
    let currentClause: string[] = [];
    
    for (let i = 0; i < terms.length; i++) {
      const term = terms[i];
      currentClause.push(term.text);
      
      // Check for coordinating conjunctions using Compromise's tags
      const tags = term.tags || [];
      const isCoordConj = (tags.includes('Conjunction') || tags.includes('Coordinate')) &&
        ['and', 'or', 'but'].includes(term.normal?.toLowerCase() || term.text.toLowerCase());
      
      if (isCoordConj && i > 0 && i < terms.length - 1) {
        const beforeText = currentClause.slice(0, -1).join(' ').trim();
        const afterTerms = terms.slice(i + 1);
        const afterText = afterTerms.map((t: any) => t.text).join(' ').trim();
        
        // Check if both sides have verbs by looking at tags
        const beforeHasVerb = currentClause.slice(0, -1).some((text, idx) => {
          const t = terms[currentClause.length - 1 - (currentClause.length - 1 - idx)];
          return t.tags?.some((tag: string) => tag.includes('Verb') || tag === 'PastTense' || tag === 'PresentTense');
        });
        
        const afterHasVerb = afterTerms.some((t: any) => 
          t.tags?.some((tag: string) => tag.includes('Verb') || tag === 'PastTense' || tag === 'PresentTense')
        );
        
        // Only split if both sides are independent clauses with verbs
        if (beforeHasVerb && afterHasVerb && beforeText.length > 5 && afterText.length > 5) {
          clauses.push(beforeText);
          currentClause = [];
          continue;
        }
      }
    }
    
    // Add remaining text
    if (currentClause.length > 0) {
      const remaining = currentClause.join(' ').trim();
      if (remaining.length > 0) {
        clauses.push(remaining);
      }
    }
    
    // If we split, return the clauses; otherwise return original
    return clauses.length > 1 ? clauses : [clauseObj.text];
  }

  /**
   * Split on coordinating conjunctions (and, or, but) when both sides have verbs
   * Used when we don't have pre-parsed terms from Compromise
   */
  private static splitOnCoordinatingConjunctions(text: string): string[] {
    const doc = nlp(text);
    const terms = doc.terms().json();
    
    if (terms.length === 0) {
      return [text];
    }
    
    const clauses: string[] = [];
    let currentClause: string[] = [];
    
    for (let i = 0; i < terms.length; i++) {
      const term = terms[i];
      currentClause.push(term.text);
      
      // Check for coordinating conjunctions
      const isCoordConj = ['and', 'or', 'but'].includes(term.normal?.toLowerCase() || term.text.toLowerCase());
      
      if (isCoordConj && i > 0 && i < terms.length - 1) {
        const beforeText = currentClause.slice(0, -1).join(' ').trim();
        const afterText = terms.slice(i + 1).map((t: any) => t.text).join(' ').trim();
        
        // Check if both sides have verbs
        const beforeHasVerb = nlp(beforeText).verbs().length > 0;
        const afterHasVerb = nlp(afterText).verbs().length > 0;
        
        // Only split if both sides are independent clauses
        if (beforeHasVerb && afterHasVerb && beforeText.length > 5 && afterText.length > 5) {
          clauses.push(beforeText);
          currentClause = [];
          continue;
        }
      }
    }
    
    // Add remaining text
    if (currentClause.length > 0) {
      const remaining = currentClause.join(' ').trim();
      if (remaining.length > 0) {
        clauses.push(remaining);
      }
    }
    
    // If we split, return the clauses; otherwise return original
    return clauses.length > 1 ? clauses : [text];
  }

  /**
   * Split long sentences that couldn't be split by clause detection
   */
  private static splitLongSentence(
    sentenceText: string,
    minLength: number,
    maxLength: number
  ): string[] {
    // Try splitting on commas first (for lists and descriptive phrases)
    const parts = sentenceText.split(/,\s+/).filter(p => p.trim().length > 0);

    if (parts.length > 1) {
      const segments: string[] = [];
      let currentSegment = '';

      for (const part of parts) {
        const proposedLength = currentSegment.length + (currentSegment ? 2 : 0) + part.length;
        
        if (proposedLength <= maxLength) {
          currentSegment = currentSegment ? currentSegment + ', ' + part : part;
        } else {
          // Current segment would exceed max - save it and start new one
          if (currentSegment.length > 0) {
            segments.push(currentSegment);
          }
          
          // If this single part is too long, split it further
          if (part.length > maxLength) {
            const words = part.split(/\s+/);
            let wordSegment = '';
            
            for (const word of words) {
              if (wordSegment.length + word.length + 1 <= maxLength) {
                wordSegment = wordSegment ? wordSegment + ' ' + word : word;
              } else {
                if (wordSegment.length > 0) {
                  segments.push(wordSegment);
                }
                wordSegment = word;
              }
            }
            
            currentSegment = wordSegment;
          } else {
            currentSegment = part;
          }
        }
      }

      if (currentSegment.length > 0) {
        segments.push(currentSegment);
      }

      return segments.length > 0 ? segments : [sentenceText];
    }

    // No commas - split by spaces to enforce max length
    if (sentenceText.length > maxLength) {
      const words = sentenceText.split(/\s+/);
      const segments: string[] = [];
      let currentSegment = '';

      for (const word of words) {
        if (currentSegment.length + word.length + 1 <= maxLength) {
          currentSegment = currentSegment ? currentSegment + ' ' + word : word;
        } else {
          if (currentSegment.length > 0) {
            segments.push(currentSegment);
          }
          currentSegment = word;
        }
      }

      if (currentSegment.length > 0) {
        segments.push(currentSegment);
      }

      return segments.length > 0 ? segments : [sentenceText];
    }

    // Short enough - return as is
    return [sentenceText];
  }

  /**
   * Merge adjacent short segments to avoid over-fragmentation
   */
  private static mergeShortSegments(
    segments: DreamSegment[],
    minLength: number
  ): DreamSegment[] {
    if (segments.length === 0) {
      return [];
    }

    const merged: DreamSegment[] = [];
    let currentMerged: DreamSegment | null = null;

    for (const segment of segments) {
      if (segment.text.length < minLength && currentMerged) {
        // Merge with current accumulator
        currentMerged.text = currentMerged.text + ' ' + segment.text;
        currentMerged.originalPosition.end = segment.originalPosition.end;
        currentMerged.type = 'clause'; // Merged segments become clauses
      } else if (segment.text.length < minLength && merged.length > 0) {
        // Merge with previous completed segment
        const lastSegment = merged[merged.length - 1];
        lastSegment.text = lastSegment.text + ' ' + segment.text;
        lastSegment.originalPosition.end = segment.originalPosition.end;
        lastSegment.type = 'clause';
      } else {
        // Complete any pending merge
        if (currentMerged) {
          merged.push(currentMerged);
          currentMerged = null;
        }

        // Add new segment or start accumulating
        if (segment.text.length >= minLength) {
          merged.push(segment);
        } else {
          currentMerged = { ...segment };
        }
      }
    }

    // Complete final merge - IMPORTANT: always add remaining segment even if short
    if (currentMerged) {
      // If we have accumulated segments, add them even if still below minLength
      // This ensures we don't lose content for edge cases where all text is short
      merged.push(currentMerged);
    }

    // Edge case: if we still have no merged segments (all were too short individually)
    // but we had input segments, merge them all together
    if (merged.length === 0 && segments.length > 0) {
      const allText = segments.map(s => s.text).join(' ');
      merged.push({
        text: allText,
        index: 0,
        type: 'clause',
        originalPosition: {
          start: segments[0].originalPosition.start,
          end: segments[segments.length - 1].originalPosition.end,
        },
      });
    }

    // Renumber indices
    return merged.map((segment, index) => ({
      ...segment,
      index,
    }));
  }

  /**
   * Find position of substring in original text
   */
  private static findPosition(
    segment: string,
    originalText: string
  ): { start: number; end: number } {
    const start = originalText.indexOf(segment);
    if (start === -1) {
      // Fallback: approximate position
      return { start: 0, end: segment.length };
    }
    return { start, end: start + segment.length };
  }

  /**
   * Log metrics for analysis
   */
  private static logMetrics(metrics: EnhancedSegmentationMetrics): void {
    this.metrics.push(metrics);

    // Keep only last 100 entries in memory
    if (this.metrics.length > 100) {
      this.metrics = this.metrics.slice(-100);
    }

    // Metrics are logged via getMetrics() / getMetricsSummary() methods
    // Can be accessed programmatically for debugging/analysis
  }

  /**
   * Get collected metrics (for analysis/debugging)
   */
  static getMetrics(): EnhancedSegmentationMetrics[] {
    return [...this.metrics];
  }

  /**
   * Clear metrics
   */
  static clearMetrics(): void {
    this.metrics = [];
  }

  /**
   * Get metrics summary
   */
  static getMetricsSummary(): {
    totalDreams: number;
    compromiseUsage: number;
    regexUsage: number;
    avgProcessingTimeMs: number;
    avgSegmentCount: number;
    compromiseFailureRate: number;
  } {
    if (this.metrics.length === 0) {
      return {
        totalDreams: 0,
        compromiseUsage: 0,
        regexUsage: 0,
        avgProcessingTimeMs: 0,
        avgSegmentCount: 0,
        compromiseFailureRate: 0,
      };
    }

    const compromiseUsage = this.metrics.filter(m => m.parserUsed === 'compromise').length;
    const regexUsage = this.metrics.filter(m => m.parserUsed === 'regex-fallback').length;
    const avgProcessingTimeMs =
      this.metrics.reduce((sum, m) => sum + m.processingTimeMs, 0) / this.metrics.length;
    const avgSegmentCount =
      this.metrics.reduce((sum, m) => sum + m.segmentCount, 0) / this.metrics.length;
    const compromiseFailures = this.metrics.filter(m => m.compromiseFailed).length;

    return {
      totalDreams: this.metrics.length,
      compromiseUsage,
      regexUsage,
      avgProcessingTimeMs,
      avgSegmentCount,
      compromiseFailureRate: compromiseFailures / this.metrics.length,
    };
  }

  /**
   * Get contextual information for a segment (same as original)
   */
  static getSegmentContext(
    segment: DreamSegment,
    allSegments: DreamSegment[]
  ): {
    previousSegment?: DreamSegment;
    nextSegment?: DreamSegment;
    contextualText: string;
  } {
    return DreamSentencizer.getSegmentContext(segment, allSegments);
  }
}

